<template>
  <div class="inv-dash">

    <!-- Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Inventory Dashboard</h5>
        <p class="page-sub">Stock levels, sales performance &amp; expiry alerts at a glance</p>
      </div>
      <div class="header-actions">
        <router-link :to="$url('/all-products')" class="btn-outline">
          <font-awesome-icon icon="boxes" class="me-1" /> All Products
        </router-link>
        <button class="btn-icon-refresh" @click="load" :disabled="loading" title="Refresh">
          <font-awesome-icon icon="redo" />
        </button>
      </div>
    </div>

    <div v-if="loading" class="full-loader">
      <div class="spinner"></div><p>Loading dashboard…</p>
    </div>

    <template v-else>

      <!-- ── KPI Cards ─────────────────────────────────────────────── -->
      <div class="kpi-row">
        <div class="kpi-card kpi-card--blue">
          <div class="kpi-icon"><font-awesome-icon icon="boxes" /></div>
          <div class="kpi-body">
            <span class="kpi-value">{{ stats.total_products ?? 0 }}</span>
            <span class="kpi-label">Total Products</span>
          </div>
        </div>
        <div class="kpi-card kpi-card--teal">
          <div class="kpi-icon"><font-awesome-icon icon="coins" /></div>
          <div class="kpi-body">
            <span class="kpi-value">{{ formatMoney(stats.stock_value ?? 0) }}</span>
            <span class="kpi-label">Stock Value</span>
          </div>
        </div>
        <div class="kpi-card kpi-card--green">
          <div class="kpi-icon"><font-awesome-icon icon="chart-line" /></div>
          <div class="kpi-body">
            <span class="kpi-value">{{ formatMoney(stats.sales_this_month ?? 0) }}</span>
            <span class="kpi-label">Sales This Month</span>
          </div>
        </div>
        <div class="kpi-card kpi-card--purple">
          <div class="kpi-icon"><font-awesome-icon icon="shopping-cart" /></div>
          <div class="kpi-body">
            <span class="kpi-value">{{ formatMoney(stats.purchases_this_month ?? 0) }}</span>
            <span class="kpi-label">Purchases This Month</span>
          </div>
        </div>
        <div class="kpi-card kpi-card--amber">
          <div class="kpi-icon"><font-awesome-icon icon="exclamation-triangle" /></div>
          <div class="kpi-body">
            <span class="kpi-value">{{ stats.low_stock_count ?? 0 }}</span>
            <span class="kpi-label">Low Stock Items</span>
          </div>
        </div>
        <div class="kpi-card kpi-card--red">
          <div class="kpi-icon"><font-awesome-icon icon="ban" /></div>
          <div class="kpi-body">
            <span class="kpi-value">{{ stats.out_of_stock_count ?? 0 }}</span>
            <span class="kpi-label">Out of Stock</span>
          </div>
        </div>
      </div>

      <!-- ── Row 1: Sales vs Purchases Trend + Top Products ────────── -->
      <div class="chart-row chart-row--2">
        <div class="chart-card">
          <div class="chart-card-header">
            <span class="chart-card-title">Sales vs Purchases — Last 6 Months</span>
          </div>
          <GChart
            v-if="salesVsPurchasesData.length > 1"
            type="ColumnChart"
            :data="salesVsPurchasesData"
            :options="salesVsPurchasesOpts"
            style="width:100%;height:260px"
          />
          <div v-else class="chart-empty">No transaction data yet</div>
        </div>

        <div class="chart-card">
          <div class="chart-card-header">
            <span class="chart-card-title">Top Selling Products</span>
            <span class="chart-card-sub">By quantity this year</span>
          </div>
          <GChart
            v-if="topProductsData.length > 1"
            type="BarChart"
            :data="topProductsData"
            :options="topProductsOpts"
            style="width:100%;height:260px"
          />
          <div v-else class="chart-empty">No sales data yet</div>
        </div>
      </div>

      <!-- ── Row 2: Stock Health Donut + Monthly Revenue Trend ──────── -->
      <div class="chart-row chart-row--3">
        <div class="chart-card chart-card--sm">
          <div class="chart-card-header">
            <span class="chart-card-title">Stock Health</span>
          </div>
          <GChart
            v-if="stockHealthData.length > 1"
            type="PieChart"
            :data="stockHealthData"
            :options="stockHealthOpts"
            style="width:100%;height:220px"
          />
          <div v-else class="chart-empty">No stock data</div>
          <div class="legend-row">
            <div class="legend-item">
              <span class="legend-dot" style="background:#22c55e"></span>
              <span class="legend-label">Healthy</span>
              <span class="legend-val">{{ healthyCount }}</span>
            </div>
            <div class="legend-item">
              <span class="legend-dot" style="background:#f97316"></span>
              <span class="legend-label">Low Stock</span>
              <span class="legend-val">{{ stats.low_stock_count ?? 0 }}</span>
            </div>
            <div class="legend-item">
              <span class="legend-dot" style="background:#ef4444"></span>
              <span class="legend-label">Out of Stock</span>
              <span class="legend-val">{{ stats.out_of_stock_count ?? 0 }}</span>
            </div>
          </div>
        </div>

        <div class="chart-card chart-card--lg">
          <div class="chart-card-header">
            <span class="chart-card-title">Monthly Sales Revenue Trend</span>
          </div>
          <GChart
            v-if="salesTrendData.length > 1"
            type="AreaChart"
            :data="salesTrendData"
            :options="salesTrendOpts"
            style="width:100%;height:220px"
          />
          <div v-else class="chart-empty">No sales data yet</div>
        </div>
      </div>

      <!-- ── Row 3: Low Stock + Expiring Soon ──────────────────────── -->
      <div class="table-row">
        <div class="table-card">
          <div class="table-card-header">
            <span class="table-card-title">
              <font-awesome-icon icon="exclamation-triangle" class="me-1 icon-amber" />
              Low Stock Alert
            </span>
            <span class="badge-pill badge-pill--amber">{{ stats.low_stock_count ?? 0 }} items</span>
          </div>
          <div v-if="!lowStockItems.length" class="table-empty">
            <font-awesome-icon icon="check-circle" class="me-1 icon-green" /> All stock levels are healthy
          </div>
          <table v-else class="mini-table">
            <thead>
              <tr><th>Product</th><th class="num-col">On Hand</th><th class="num-col">Min Level</th><th>Status</th></tr>
            </thead>
            <tbody>
              <tr v-for="item in lowStockItems" :key="item.id">
                <td>
                  <div class="fw-600">{{ item.product?.product_name ?? '—' }}</div>
                  <div v-if="item.product?.product_code" class="text-mono">{{ item.product.product_code }}</div>
                </td>
                <td class="num-col">{{ item.quantity }}</td>
                <td class="num-col">{{ item.warning_quantity }}</td>
                <td>
                  <span class="stock-pill stock-pill--amber">Low</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="table-card">
          <div class="table-card-header">
            <span class="table-card-title">
              <font-awesome-icon icon="calendar-alt" class="me-1 icon-red" />
              Expiring Soon
            </span>
            <span class="badge-pill badge-pill--red">{{ stats.expiring_soon_count ?? 0 }} items</span>
          </div>
          <div v-if="!expiringItems.length" class="table-empty">
            <font-awesome-icon icon="check-circle" class="me-1 icon-green" /> No items expiring in the next 30 days
          </div>
          <table v-else class="mini-table">
            <thead>
              <tr><th>Product</th><th class="num-col">Qty</th><th>Expires</th><th>Days Left</th></tr>
            </thead>
            <tbody>
              <tr v-for="item in expiringItems" :key="item.id">
                <td>
                  <div class="fw-600">{{ item.product?.product_name ?? '—' }}</div>
                  <div v-if="item.product?.product_code" class="text-mono">{{ item.product.product_code }}</div>
                </td>
                <td class="num-col">{{ item.quantity }}</td>
                <td>{{ formatDate(item.best_before) }}</td>
                <td>
                  <span :class="['days-pill', daysClass(item.best_before)]">
                    {{ daysLeft(item.best_before) }}d
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';

export default {
  name: 'InventoryDashboard',
  setup() { return { toast: useToast() }; },
  data() { return { loading: false, stats: {} }; },
  computed: {
    lowStockItems()  { return this.stats.low_stock_items  ?? []; },
    expiringItems()  { return this.stats.expiring_items   ?? []; },
    healthyCount() {
      const total = this.stats.total_products ?? 0;
      return Math.max(0, total - (this.stats.low_stock_count ?? 0) - (this.stats.out_of_stock_count ?? 0));
    },

    // ── Google Charts data ─────────────────────────────────────────
    salesVsPurchasesData() {
      const sales     = this.stats.monthly_sales     ?? [];
      const purchases = this.stats.monthly_purchases ?? [];
      if (!sales.length) return [['Month', 'Sales', 'Purchases']];
      return [
        ['Month', 'Sales', 'Purchases'],
        ...sales.map((s, i) => [s.month, s.amount, purchases[i]?.amount ?? 0]),
      ];
    },
    topProductsData() {
      const rows = (this.stats.top_products ?? []).map(p => [p.product_name, Number(p.qty_sold)]);
      return rows.length ? [['Product', 'Qty Sold'], ...rows] : [['Product', 'Qty Sold']];
    },
    stockHealthData() {
      const healthy  = this.healthyCount;
      const low      = this.stats.low_stock_count    ?? 0;
      const out      = this.stats.out_of_stock_count ?? 0;
      if (!healthy && !low && !out) return [['Status', 'Count']];
      const rows = [];
      if (healthy > 0) rows.push(['Healthy', healthy]);
      if (low > 0)     rows.push(['Low Stock', low]);
      if (out > 0)     rows.push(['Out of Stock', out]);
      return [['Status', 'Count'], ...rows];
    },
    salesTrendData() {
      const rows = (this.stats.monthly_sales ?? []).map(s => [s.month, s.amount]);
      return rows.length ? [['Month', 'Revenue'], ...rows] : [['Month', 'Revenue']];
    },

    // ── Chart options ──────────────────────────────────────────────
    salesVsPurchasesOpts() {
      return {
        colors: ['#22c55e', '#f97316'],
        legend: { position: 'top', textStyle: { fontSize: 11, color: '#7a90b0' } },
        chartArea: { width: '82%', height: '72%' },
        hAxis: { textStyle: { fontSize: 10, color: '#7a90b0' } },
        vAxis: { textStyle: { fontSize: 11, color: '#7a90b0' }, minValue: 0, gridlines: { color: '#f0f4f8' }, format: 'short' },
        backgroundColor: 'transparent',
        bar: { groupWidth: '65%' },
      };
    },
    topProductsOpts() {
      return {
        colors: ['#004AAD'],
        legend: { position: 'none' },
        chartArea: { width: '60%', height: '82%' },
        hAxis: { textStyle: { fontSize: 11, color: '#7a90b0' }, minValue: 0, format: '0' },
        vAxis: { textStyle: { fontSize: 10, color: '#7a90b0' } },
        backgroundColor: 'transparent',
        bar: { groupWidth: '60%' },
      };
    },
    stockHealthOpts() {
      return {
        pieHole: 0.45,
        colors: ['#22c55e', '#f97316', '#ef4444'],
        legend: { position: 'none' },
        chartArea: { width: '90%', height: '88%' },
        backgroundColor: 'transparent',
        tooltip: { showColorCode: true },
      };
    },
    salesTrendOpts() {
      return {
        colors: ['#22c55e'],
        legend: { position: 'none' },
        chartArea: { width: '88%', height: '78%' },
        hAxis: { textStyle: { fontSize: 10, color: '#7a90b0' } },
        vAxis: { textStyle: { fontSize: 11, color: '#7a90b0' }, minValue: 0, gridlines: { color: '#f0f4f8' }, format: 'short' },
        backgroundColor: 'transparent',
        areaOpacity: 0.12,
        pointSize: 5,
        lineWidth: 2,
      };
    },
  },
  created() { this.load(); },
  methods: {
    async load() {
      this.loading = true;
      try {
        const { data } = await axios.get('/inventory/dashboard');
        this.stats = data.data ?? {};
      } catch { this.toast.error('Failed to load inventory dashboard.'); }
      finally { this.loading = false; }
    },
    formatDate(d) {
      return d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
    },
    formatMoney(v) {
      return new Intl.NumberFormat('en-NG', { notation: 'compact', maximumFractionDigits: 1 }).format(v);
    },
    daysLeft(d) { return d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null; },
    daysClass(d) {
      const n = this.daysLeft(d);
      if (n === null) return '';
      if (n <= 7)  return 'days-pill--red';
      if (n <= 14) return 'days-pill--amber';
      return 'days-pill--green';
    },
  },
};
</script>

<style scoped>
/* ── Layout ─────────────────────────────────────────────────────────── */
.inv-dash { padding-bottom: 32px; }
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:24px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.header-actions { display:flex; align-items:center; gap:10px; }
.me-1 { margin-right:4px; }

/* ── Full loader ─────────────────────────────────────────────────── */
.full-loader { display:flex; flex-direction:column; align-items:center; justify-content:center; min-height:320px; gap:12px; color:#7a90b0; }

/* ── KPI row ─────────────────────────────────────────────────────── */
.kpi-row { display:grid; grid-template-columns:repeat(6, 1fr); gap:14px; margin-bottom:20px; }
.kpi-card { display:flex; align-items:center; gap:12px; background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:16px 18px; position:relative; overflow:hidden; }
.kpi-card::before { content:''; position:absolute; top:0; left:0; width:4px; height:100%; border-radius:4px 0 0 4px; }
.kpi-card--blue::before   { background:#004AAD; }
.kpi-card--teal::before   { background:#0d9488; }
.kpi-card--green::before  { background:#22c55e; }
.kpi-card--purple::before { background:#7c3aed; }
.kpi-card--amber::before  { background:#f97316; }
.kpi-card--red::before    { background:#dc2626; }
.kpi-icon { width:38px; height:38px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
.kpi-card--blue   .kpi-icon { background:#eff6ff; color:#004AAD; }
.kpi-card--teal   .kpi-icon { background:#f0fdfa; color:#0d9488; }
.kpi-card--green  .kpi-icon { background:#f0fdf4; color:#16a34a; }
.kpi-card--purple .kpi-icon { background:#f5f3ff; color:#7c3aed; }
.kpi-card--amber  .kpi-icon { background:#fff7ed; color:#f97316; }
.kpi-card--red    .kpi-icon { background:#fef2f2; color:#dc2626; }
.kpi-body { min-width:0; }
.kpi-value { display:block; font-size:20px; font-weight:800; color:#1a2b45; line-height:1.1; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.kpi-label { display:block; font-size:11px; color:#7a90b0; margin-top:3px; }

/* ── Chart rows ──────────────────────────────────────────────────── */
.chart-row { display:grid; gap:16px; margin-bottom:20px; }
.chart-row--2 { grid-template-columns:1fr 1fr; }
.chart-row--3 { grid-template-columns:1fr 2fr; }
.chart-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:20px; }
.chart-card-header { display:flex; align-items:baseline; justify-content:space-between; margin-bottom:12px; }
.chart-card-title  { font-size:14px; font-weight:700; color:#1a2b45; }
.chart-card-sub    { font-size:12px; color:#7a90b0; }
.chart-empty { display:flex; align-items:center; justify-content:center; height:200px; color:#aab8cc; font-size:13px; }

/* Legend */
.legend-row { display:flex; flex-wrap:wrap; gap:8px 16px; margin-top:12px; }
.legend-item { display:flex; align-items:center; gap:6px; }
.legend-dot  { width:10px; height:10px; border-radius:50%; flex-shrink:0; }
.legend-label { font-size:12px; color:#7a90b0; }
.legend-val   { font-size:12px; font-weight:700; color:#1a2b45; }

/* ── Table row ───────────────────────────────────────────────────── */
.table-row { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
.table-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; overflow:hidden; }
.table-card-header { display:flex; align-items:center; justify-content:space-between; padding:16px 20px; border-bottom:1px solid #f0f4f8; }
.table-card-title  { font-size:14px; font-weight:700; color:#1a2b45; display:flex; align-items:center; gap:6px; }
.table-card-sub    { font-size:12px; color:#7a90b0; }
.table-empty { padding:28px 20px; text-align:center; color:#aab8cc; font-size:13px; }
.mini-table { width:100%; border-collapse:collapse; }
.mini-table th { padding:9px 16px; font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; text-align:left; white-space:nowrap; }
.mini-table td { padding:11px 16px; font-size:13px; color:#1a2b45; border-bottom:1px solid #f0f4f8; vertical-align:middle; }
.mini-table tbody tr:last-child td { border-bottom:none; }
.num-col { text-align:right; font-variant-numeric:tabular-nums; }
.fw-600 { font-weight:600; }
.text-mono { font-family:monospace; font-size:11px; color:#7a90b0; }

/* Pills */
.stock-pill { display:inline-block; font-size:11.5px; font-weight:600; border-radius:20px; padding:2px 10px; }
.stock-pill--amber { background:#fff7ed; color:#c2410c; }
.badge-pill { display:inline-block; font-size:11.5px; font-weight:700; border-radius:20px; padding:3px 10px; }
.badge-pill--amber { background:#fff7ed; color:#c2410c; }
.badge-pill--red   { background:#fee2e2; color:#dc2626; }
.days-pill { display:inline-block; font-size:11.5px; font-weight:700; border-radius:20px; padding:2px 9px; }
.days-pill--green { background:#dcfce7; color:#15803d; }
.days-pill--amber { background:#fff7ed; color:#c2410c; }
.days-pill--red   { background:#fee2e2; color:#dc2626; }

/* Icon tints */
.icon-amber { color:#f97316; }
.icon-red   { color:#dc2626; }
.icon-green { color:#22c55e; }

/* ── Buttons ─────────────────────────────────────────────────────── */
.btn-outline { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; border:1.5px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; text-decoration:none; }
.btn-outline:hover { background:#f4f7fb; }
.btn-icon-refresh { width:38px; height:38px; border:1.5px solid #dde5f0; border-radius:8px; background:#fff; color:#7a90b0; display:flex; align-items:center; justify-content:center; cursor:pointer; font-size:14px; }
.btn-icon-refresh:hover:not(:disabled) { border-color:#004AAD; color:#004AAD; }
.btn-icon-refresh:disabled { opacity:.5; cursor:not-allowed; }

/* ── Spinner ─────────────────────────────────────────────────────── */
.spinner { width:36px; height:36px; border:3px solid #e4eaf2; border-top-color:#004AAD; border-radius:50%; animation:spin .7s linear infinite; }
@keyframes spin { to { transform:rotate(360deg); } }

/* ── Responsive ──────────────────────────────────────────────────── */
@media (max-width:1400px) {
  .kpi-row { grid-template-columns:repeat(3, 1fr); }
}
@media (max-width:1024px) {
  .chart-row--2, .chart-row--3, .table-row { grid-template-columns:1fr; }
}
@media (max-width:640px) {
  .kpi-row { grid-template-columns:1fr 1fr; }
}
</style>
